import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchHistory = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/search-history/list`, {
        withCredentials: true
      });
      setHistory(response.data.history);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    try {
      await axios.delete(`${API}/search-history/clear`, {
        withCredentials: true
      });
      setHistory([]);
      toast.success('History cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const getStatusClass = (color) => {
    if (color === 'red') return 'bg-red-500';
    if (color === 'yellow') return 'bg-yellow-500';
    if (color === 'green') return 'bg-green-500';
    return 'bg-gray-500';
  };

  const getStatusLabel = (status) => {
    if (status === 'not_compatible') return 'Not Compatible';
    if (status === 'compatible') return 'Compatible';
    if (status === 'caution') return 'Caution';
    return status;
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Search History
            </h1>
          </div>
          {history.length > 0 && (
            <Button
              data-testid="clear-history-button"
              onClick={handleClearHistory}
              variant="ghost"
              className="text-white hover:bg-red-500/20"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </Button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-12">
        {loading ? (
          <div className="flex justify-center">
            <div className="spinner"></div>
          </div>
        ) : history.length === 0 ? (
          <Card className="glass border-white/20">
            <CardContent className="py-12 text-center">
              <Search className="w-16 h-16 mx-auto text-white/50 mb-4" />
              <p className="text-white text-lg">No search history yet</p>
              <p className="text-white/70 text-sm mt-2">Your chemical searches will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="history-list">
            {history.map((item, index) => (
              <Card key={index} className="glass border-white/20 card-hover">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-xl">{item.chemical_name}</CardTitle>
                    <span className={`${getStatusClass(item.compatibility_color)} px-4 py-2 rounded-full text-white text-sm font-semibold`}>
                      {getStatusLabel(item.compatibility_result)}
                    </span>
                  </div>
                  <p className="text-white/70 text-sm">
                    {new Date(item.search_date).toLocaleString()}
                  </p>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchHistory;
